import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { createPortal } from 'react-dom';
import { CustomPurchaseSlotContext } from '@modules/screen-view-v2/customPurchaseSlot';
import type { CustomPurchaseSlotInput, CustomPurchaseSlotMount } from '@modules/screen-view-v2/customPurchaseSlot';

interface Mount {
  input: CustomPurchaseSlotInput;
  carrier: HTMLDivElement;
  dispose(): void;
}

/** The authored empty DIV retains its CSS and position in the ShadowRoot. A named
 * slot projects a document-owned carrier there so provider document lookup works. */
export function useCustomPurchaseSlots(screenId: string, hostRef: RefObject<HTMLDivElement | null>) {
  const renderer = useContext(CustomPurchaseSlotContext);
  const available = useRef(false); available.current = !!renderer;
  const currentScreen = useRef(screenId); currentScreen.current = screenId;
  const mounts = useRef(new Set<Mount>());
  const [mounted, setMounted] = useState<Mount[]>([]);
  const clear = useCallback(() => { for (const mount of [...mounts.current]) mount.dispose(); }, []);
  useEffect(() => clear, [screenId, !!renderer, clear]);

  const mount: CustomPurchaseSlotMount = useCallback((target, input) => {
    const host = hostRef.current;
    if (!available.current || currentScreen.current !== screenId || !host?.isConnected ||
      host.getRootNode() !== host.ownerDocument || !host.shadowRoot ||
      target.getRootNode() !== host.shadowRoot || !target.isConnected || target.tagName !== 'DIV' ||
      target.childNodes.length || mounts.current.size) return null;
    const controller = new AbortController();
    const carrier = host.ownerDocument.createElement('div');
    const projection = host.ownerDocument.createElement('slot');
    const name = `segmently-purchase-${crypto.randomUUID()}`;
    projection.name = name; carrier.slot = name;
    // No fixed height/clipping: the complete provider footer stays in normal flow.
    carrier.style.width = '100%';
    target.append(projection); host.append(carrier);
    const entry: Mount = { carrier, input: { ...input, screenId, layoutTarget: target, signal: controller.signal },
      dispose: () => {
        if (!mounts.current.delete(entry)) return;
        controller.abort(); observer.disconnect();
        carrier.remove(); projection.remove();
        setMounted([...mounts.current]);
        input.onDispose?.();
      } };
    const observer = new MutationObserver(() => {
      if (!host.isConnected || !target.isConnected || target.getRootNode() !== host.shadowRoot ||
        projection.parentNode !== target || carrier.parentNode !== host ||
        projection.name !== name || carrier.slot !== name || carrier.assignedSlot !== projection) entry.dispose();
    });
    observer.observe(host.shadowRoot, { childList: true, subtree: true, attributes: true, attributeFilter: ['name', 'slot'] });
    observer.observe(host.ownerDocument.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ['name', 'slot'] });
    mounts.current.add(entry); setMounted([...mounts.current]);
    return entry.dispose;
  }, [hostRef, screenId]);

  const portals = renderer ? mounted.filter(entry => entry.input.screenId === screenId && !entry.input.signal.aborted)
    .map(entry => createPortal(renderer(entry.input), entry.carrier, entry.input.requestId)) : null;
  return { mount, clear, portals };
}
